import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import {
  ArrowsUpDownIcon,
  BriefcaseIcon,
  ClockIcon,
  CubeIcon,
  CubeTransparentIcon,
  CurrencyPoundIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import HeadlineStat from "../../components/shared/HeadlineStat";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import {
  BayOccupancy,
  RACKING_BAYS,
  RackedVehicle,
  buildRackedVehicles,
  summariseBays,
} from "./vehicleRackingMockData";

type SortKey =
  | "priority"
  | "daysInRacking"
  | "potentialValue"
  | "partsSaleable"
  | "bay";

const fmtInt = (n: number) => n.toLocaleString("en-GB");

/**
 * Vehicle Racking Manager — yard-floor operational dashboard for vehicles
 * that have been racked after arrival but haven't yet been dismantled.
 *
 * Sections:
 *  1. Headline tiles  — how many vehicles, capacity, vacancy, urgency.
 *  2. Racked vehicles — image, donor info, parts/jobs summary, ageing.
 *  3. Bays table      — every location in the yard with capacity, current
 *                       vehicles and remaining spaces.
 *
 * Data is deterministic mock (`vehicleRackingMockData.ts`) until the
 * `/racking/*` endpoints ship — see `docs/pending-endpoints.md`.
 */
export default function VehicleRackingManager() {
  const { setInfo } = usePageNameContext();
  const [bayFilter, setBayFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("priority");
  const [focusedBay, setFocusedBay] = useState<string | null>(null);

  useEffect(() => {
    setInfo({
      name: "Vehicle Racking Manager",
      desc: "Vehicles currently held in racking — awaiting dismantling, being worked on, or ready to clear",
    });
  }, [setInfo]);

  const vehicles = useMemo(() => buildRackedVehicles(), []);
  const bays = useMemo(() => summariseBays(vehicles), [vehicles]);
  const bayById = useMemo(() => {
    const m = new Map<string, BayOccupancy>();
    for (const b of bays) m.set(b.bay.id, b);
    return m;
  }, [bays]);

  // --- Vehicle filtering & sorting ------------------------------------------

  const filteredVehicles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return vehicles.filter((v) => {
      if (bayFilter !== "all" && v.bayId !== bayFilter) return false;
      if (priorityFilter !== "all" && v.priority !== priorityFilter) return false;
      if (statusFilter !== "all" && v.status !== statusFilter) return false;
      if (focusedBay && v.bayId !== focusedBay) return false;
      if (!needle) return true;
      return (
        v.registration.toLowerCase().includes(needle) ||
        v.stockNumber.toLowerCase().includes(needle) ||
        v.make.toLowerCase().includes(needle) ||
        v.model.toLowerCase().includes(needle)
      );
    });
  }, [vehicles, bayFilter, priorityFilter, statusFilter, query, focusedBay]);

  const sortedVehicles = useMemo(() => {
    const copy = [...filteredVehicles];
    const priorityRank: Record<RackedVehicle["priority"], number> = {
      Urgent: 0,
      High: 1,
      Normal: 2,
      Low: 3,
    };
    switch (sortKey) {
      case "priority":
        copy.sort((a, b) => {
          const p = priorityRank[a.priority] - priorityRank[b.priority];
          if (p !== 0) return p;
          return b.daysInRacking - a.daysInRacking;
        });
        break;
      case "daysInRacking":
        copy.sort((a, b) => b.daysInRacking - a.daysInRacking);
        break;
      case "potentialValue":
        copy.sort((a, b) => b.potentialValue - a.potentialValue);
        break;
      case "partsSaleable":
        copy.sort(
          (a, b) =>
            b.partsSaleable - b.partsSold - (a.partsSaleable - a.partsSold)
        );
        break;
      case "bay":
        copy.sort((a, b) => {
          if (a.bayId !== b.bayId) return a.bayId.localeCompare(b.bayId);
          return a.bayPosition - b.bayPosition;
        });
        break;
    }
    return copy;
  }, [filteredVehicles, sortKey]);

  // --- Headline stats -------------------------------------------------------

  const totalCapacity = RACKING_BAYS.reduce((s, b) => s + b.capacity, 0);
  const totalUsed = vehicles.length;
  const totalAvailable = Math.max(0, totalCapacity - totalUsed);
  const utilisation =
    totalCapacity === 0
      ? 0
      : Math.round((totalUsed / totalCapacity) * 100);

  const avgDays =
    totalUsed === 0
      ? 0
      : Math.round(
          vehicles.reduce((s, v) => s + v.daysInRacking, 0) / totalUsed
        );

  const urgentCount = vehicles.filter((v) => v.priority === "Urgent").length;
  const oldCount = vehicles.filter((v) => v.daysInRacking >= 60).length;
  const inProgressCount = vehicles.filter(
    (v) => v.status === "In Progress"
  ).length;
  const readyToClearCount = vehicles.filter(
    (v) => v.status === "Ready to Clear"
  ).length;

  const totalPotential = vehicles.reduce(
    (s, v) => s + v.potentialValue,
    0
  );

  const fullBays = bays.filter((b) => b.available === 0).length;
  const emptyBays = bays.filter((b) => b.used === 0).length;

  const zones = useMemo(() => {
    const set = new Set<string>();
    for (const b of RACKING_BAYS) set.add(b.zone);
    return [...set];
  }, []);

  return (
    <div className="mt-4 space-y-4">
      {/* ---------- Headline tiles ---------- */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <HeadlineStat
          tone="blue"
          icon={TruckIcon}
          value={fmtInt(totalUsed)}
          label="Vehicles in racking"
          sublabel={`${fmtInt(inProgressCount)} being worked on`}
        />
        <HeadlineStat
          tone="emerald"
          icon={Squares2X2Icon}
          value={`${fmtInt(utilisation)}%`}
          label="Rack utilisation"
          sublabel={`${fmtInt(totalUsed)}/${fmtInt(totalCapacity)} spaces`}
        />
        <HeadlineStat
          tone="cyan"
          icon={CubeTransparentIcon}
          value={fmtInt(totalAvailable)}
          label="Available spaces"
          sublabel={`${fmtInt(emptyBays)} empty · ${fmtInt(fullBays)} full`}
        />
        <HeadlineStat
          tone="amber"
          icon={ClockIcon}
          value={`${fmtInt(avgDays)}d`}
          label="Avg days in rack"
          sublabel={`${fmtInt(oldCount)} aged ≥ 60d`}
        />
        <HeadlineStat
          tone="rose"
          icon={ExclamationTriangleIcon}
          value={fmtInt(urgentCount)}
          label="Urgent priority"
          sublabel={`${fmtInt(readyToClearCount)} ready to clear`}
        />
        <HeadlineStat
          tone="violet"
          icon={CurrencyPoundIcon}
          value={PriceFormat(totalPotential)}
          label="Parts value racked"
          sublabel="Saleable parts still on vehicles"
        />
      </div>

      {/* ---------- Racked vehicles table ---------- */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <header className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <TruckIcon className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                Racked vehicles
              </h2>
              <p className="text-[11px] text-slate-500">
                {focusedBay
                  ? `Filtered to bay ${bayById.get(focusedBay)?.bay.name ?? focusedBay}.`
                  : "Vehicles currently in racking, awaiting or mid-dismantle."}
                {focusedBay && (
                  <button
                    type="button"
                    onClick={() => setFocusedBay(null)}
                    className="ml-2 text-primary-600 underline-offset-2 hover:underline"
                  >
                    Clear
                  </button>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <FilterSelect
              label="Bay"
              value={bayFilter}
              onChange={setBayFilter}
              options={[
                { value: "all", label: "All bays" },
                ...RACKING_BAYS.map((b) => ({
                  value: b.id,
                  label: `${b.name} — ${b.zone}`,
                })),
              ]}
              width="12rem"
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All" },
                { value: "Awaiting Dismantling", label: "Awaiting" },
                { value: "In Progress", label: "In progress" },
                { value: "Ready to Clear", label: "Ready to clear" },
              ]}
              width="10rem"
            />
            <FilterSelect
              label="Priority"
              value={priorityFilter}
              onChange={setPriorityFilter}
              options={[
                { value: "all", label: "All" },
                { value: "Urgent", label: "Urgent" },
                { value: "High", label: "High" },
                { value: "Normal", label: "Normal" },
                { value: "Low", label: "Low" },
              ]}
              width="8rem"
            />
            <FilterSelect
              label="Sort by"
              value={sortKey}
              onChange={(v) => setSortKey(v as SortKey)}
              options={[
                { value: "priority", label: "Priority" },
                { value: "daysInRacking", label: "Days in rack" },
                { value: "potentialValue", label: "Parts value" },
                { value: "partsSaleable", label: "Parts remaining" },
                { value: "bay", label: "Bay / slot" },
              ]}
              width="10rem"
            />
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Reg, stock no, make or model"
                className="w-60 rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>
        </header>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th>Vehicle</Th>
                <Th>Bay</Th>
                <Th>Racked</Th>
                <Th className="text-right">Days</Th>
                <Th>Parts</Th>
                <Th>Jobs</Th>
                <Th className="text-right">Potential</Th>
                <Th>Status</Th>
                <Th>Priority</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedVehicles.map((v) => {
                const bay = bayById.get(v.bayId)?.bay;
                const partsRemaining =
                  v.partsSaleable - v.partsSold - v.partsReserved;
                const listedPct =
                  v.partsSaleable === 0
                    ? 0
                    : Math.round((v.partsListed / v.partsSaleable) * 100);
                const soldPct =
                  v.partsSaleable === 0
                    ? 0
                    : Math.round((v.partsSold / v.partsSaleable) * 100);
                return (
                  <tr key={v.id} className="hover:bg-slate-50/70">
                    <td className="px-3 py-2 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="h-14 w-20 flex-none overflow-hidden rounded-md bg-slate-100 ring-1 ring-slate-200">
                          <img
                            src={v.imageUrl}
                            alt={`${v.make} ${v.model}`}
                            loading="lazy"
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="flex min-w-0 flex-col leading-tight">
                          <span className="truncate font-semibold text-slate-900">
                            {v.year} {v.make} {v.model}
                          </span>
                          <span className="truncate text-[11px] text-slate-500">
                            {v.derivative} · {v.colour}
                          </span>
                          <span className="mt-1 flex flex-wrap items-center gap-1">
                            <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
                              {v.registration}
                            </span>
                            <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">
                              {v.stockNumber}
                            </span>
                            <span className="text-[10px] text-slate-400">
                              {fmtInt(v.mileage)}mi · {v.fuelType}
                            </span>
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle">
                      {bay ? (
                        <div className="flex flex-col leading-tight">
                          <span className="inline-flex w-fit items-center gap-1 rounded-md bg-primary-50 px-1.5 py-0.5 text-xs font-semibold text-primary-700 ring-1 ring-inset ring-primary-200">
                            <Squares2X2Icon className="h-3 w-3" aria-hidden />
                            {bay.name} · slot {v.bayPosition}
                          </span>
                          <span className="mt-1 text-[11px] text-slate-500">
                            {bay.zone} · {bay.kind}
                          </span>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-slate-600">
                      {formatDateTime(v.rackedOn, DateTime.DATE_MED)}
                    </td>
                    <td
                      className={classNames(
                        "whitespace-nowrap px-3 py-2 align-middle text-right font-semibold tabular-nums",
                        v.daysInRacking >= 90
                          ? "text-rose-600"
                          : v.daysInRacking >= 60
                            ? "text-amber-600"
                            : "text-slate-700"
                      )}
                    >
                      {v.daysInRacking}d
                    </td>
                    <td className="min-w-[12rem] px-3 py-2 align-middle">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-[11px] text-slate-600">
                          <CubeIcon className="h-3 w-3 text-slate-400" aria-hidden />
                          <span className="font-semibold text-slate-800">
                            {partsRemaining}
                          </span>
                          <span className="text-slate-400">/</span>
                          <span>{v.partsSaleable} saleable</span>
                          <span className="text-slate-300">·</span>
                          <span className="text-emerald-700">
                            {v.partsSold} sold
                          </span>
                          {v.partsReserved > 0 && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="text-amber-700">
                                {v.partsReserved} reserved
                              </span>
                            </>
                          )}
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                          <div className="flex h-full">
                            <div
                              className="h-full bg-emerald-500"
                              style={{ width: `${soldPct}%` }}
                              title={`${soldPct}% sold`}
                            />
                            <div
                              className="h-full bg-blue-400"
                              style={{
                                width: `${Math.max(0, listedPct - soldPct)}%`,
                              }}
                              title={`${listedPct - soldPct}% listed`}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle">
                      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">
                        <BriefcaseIcon className="h-3 w-3" aria-hidden />
                        {v.jobsInProgress > 0
                          ? `${v.jobsInProgress} active`
                          : `${v.jobsPending} pending`}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-right font-semibold tabular-nums text-slate-900">
                      {PriceFormat(v.potentialValue)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle">
                      <StatusPill status={v.status} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle">
                      <PriorityPill priority={v.priority} />
                    </td>
                  </tr>
                );
              })}

              {sortedVehicles.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    No vehicles match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500">
          Showing{" "}
          <span className="font-semibold text-slate-700">
            {fmtInt(sortedVehicles.length)}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-slate-700">
            {fmtInt(vehicles.length)}
          </span>{" "}
          vehicles.
        </footer>
      </section>

      {/* ---------- Racking bays table ---------- */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <Squares2X2Icon className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                Racking locations
              </h2>
              <p className="text-[11px] text-slate-500">
                {zones.length} zones · {fmtInt(RACKING_BAYS.length)} bays ·{" "}
                {fmtInt(totalCapacity)} spaces
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <LegendDot className="bg-emerald-500" label="Available" />
            <LegendDot className="bg-amber-400" label="Nearly full" />
            <LegendDot className="bg-rose-500" label="At capacity" />
          </div>
        </header>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th>Bay</Th>
                <Th>Zone</Th>
                <Th>Type</Th>
                <Th className="min-w-[14rem]">Occupancy</Th>
                <Th className="text-right">Used</Th>
                <Th className="text-right">Available</Th>
                <Th className="text-right">Avg days</Th>
                <Th className="text-right">Oldest</Th>
                <Th>Vehicles</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {bays
                .slice()
                .sort((a, b) => b.utilisation - a.utilisation)
                .map((b) => (
                  <tr
                    key={b.bay.id}
                    className={classNames(
                      "hover:bg-slate-50/70",
                      focusedBay === b.bay.id ? "bg-primary-50/40" : ""
                    )}
                  >
                    <td className="whitespace-nowrap px-3 py-2 align-middle">
                      <div className="flex flex-col leading-tight">
                        <span className="font-semibold text-slate-900">
                          {b.bay.name}
                        </span>
                        {b.bay.notes && (
                          <span className="text-[11px] text-slate-500">
                            {b.bay.notes}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-slate-700">
                      {b.bay.zone}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-slate-600">
                      {b.bay.kind}
                    </td>
                    <td className="min-w-[14rem] px-3 py-2 align-middle">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                          <div
                            className={classNames(
                              "h-full rounded-full transition-[width] duration-500",
                              b.utilisation >= 100
                                ? "bg-rose-500"
                                : b.utilisation >= 75
                                  ? "bg-amber-400"
                                  : "bg-emerald-500"
                            )}
                            style={{
                              width: `${Math.max(
                                b.utilisation,
                                b.used > 0 ? 6 : 0
                              )}%`,
                            }}
                          />
                        </div>
                        <span className="w-10 flex-none text-right text-[11px] tabular-nums text-slate-500">
                          {b.utilisation}%
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-right font-semibold tabular-nums text-slate-900">
                      {b.used} / {b.bay.capacity}
                    </td>
                    <td
                      className={classNames(
                        "whitespace-nowrap px-3 py-2 align-middle text-right font-semibold tabular-nums",
                        b.available === 0
                          ? "text-rose-600"
                          : b.available <= 1
                            ? "text-amber-600"
                            : "text-emerald-700"
                      )}
                    >
                      {b.available}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-right tabular-nums text-slate-700">
                      {b.used === 0 ? "—" : `${b.avgDays}d`}
                    </td>
                    <td
                      className={classNames(
                        "whitespace-nowrap px-3 py-2 align-middle text-right tabular-nums",
                        b.oldestDays >= 90
                          ? "text-rose-600"
                          : b.oldestDays >= 60
                            ? "text-amber-600"
                            : "text-slate-700"
                      )}
                    >
                      {b.used === 0 ? "—" : `${b.oldestDays}d`}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      {b.vehicles.length > 0 ? (
                        <span className="inline-flex flex-wrap items-center gap-1">
                          {b.vehicles.slice(0, 4).map((v) => (
                            <span
                              key={v.id}
                              className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700"
                              title={`${v.year} ${v.make} ${v.model} · ${v.registration}`}
                            >
                              {v.registration}
                            </span>
                          ))}
                          {b.vehicles.length > 4 && (
                            <span className="text-[10px] text-slate-500">
                              +{b.vehicles.length - 4}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[11px] italic text-slate-400">
                          Empty
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-right">
                      <button
                        type="button"
                        onClick={() =>
                          setFocusedBay(
                            focusedBay === b.bay.id ? null : b.bay.id
                          )
                        }
                        disabled={b.used === 0}
                        className={classNames(
                          "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition",
                          b.used === 0
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                            : focusedBay === b.bay.id
                              ? "border-primary-300 bg-primary-50 text-primary-700"
                              : "border-slate-300 bg-white text-slate-700 hover:border-primary-300 hover:text-primary-700"
                        )}
                      >
                        <ArrowsUpDownIcon className="h-3 w-3" aria-hidden />
                        {focusedBay === b.bay.id ? "Viewing" : "View"}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr className="text-sm font-semibold text-slate-700">
                <td className="px-3 py-2" colSpan={4}>
                  Totals
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtInt(totalUsed)} / {fmtInt(totalCapacity)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtInt(totalAvailable)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtInt(avgDays)}d
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentational sub-components
// ---------------------------------------------------------------------------

function Th({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={classNames(
        "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500",
        className ?? ""
      )}
    >
      {children}
    </th>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  width,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  width?: string;
}) {
  return (
    <div
      className="flex flex-col rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-1.5"
      style={width ? { width } : undefined}
    >
      <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const STATUS_TONES: Record<RackedVehicle["status"], string> = {
  "Awaiting Dismantling": "bg-slate-100 text-slate-700 ring-slate-200",
  "In Progress":          "bg-blue-50 text-blue-700 ring-blue-200",
  "Ready to Clear":       "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

function StatusPill({ status }: { status: RackedVehicle["status"] }) {
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
        STATUS_TONES[status]
      )}
    >
      {status}
    </span>
  );
}

const PRIORITY_TONES: Record<RackedVehicle["priority"], string> = {
  Urgent: "bg-rose-50 text-rose-700 ring-rose-200",
  High:   "bg-amber-50 text-amber-700 ring-amber-200",
  Normal: "bg-slate-50 text-slate-600 ring-slate-200",
  Low:    "bg-slate-50 text-slate-400 ring-slate-200",
};

function PriorityPill({ priority }: { priority: RackedVehicle["priority"] }) {
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
        PRIORITY_TONES[priority]
      )}
    >
      {priority}
    </span>
  );
}

function LegendDot({
  className,
  label,
}: {
  className: string;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={classNames("h-2 w-2 rounded-full", className)}
        aria-hidden
      />
      {label}
    </span>
  );
}
